import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { apiFetch } from "@/lib/api-client";

export type CompetitorPostFormat = "REEL" | "CAROUSEL" | "IMAGE" | "VIDEO";

export interface CompetitorPost {
  id: string;
  format: CompetitorPostFormat;
  caption: string | null;
  permalink: string | null;
  likeCount: number | null;
  commentsCount: number | null;
  timestamp: string | null;
  engagement: number;
}

export interface CompetitorInsights {
  windowDays: number;
  postsInWindow: number;
  postsPerWeek: number;
  formatMix: Record<CompetitorPostFormat, number>;
  formatShare: Record<CompetitorPostFormat, number>;
  medianEngagement: number | null;
  medianLikes: number | null;
  medianComments: number | null;
  engagementRate: number | null;
  topPosts: CompetitorPost[];
  windowTruncated: boolean;
}

export interface WatchedAccount {
  id: string;
  username: string;
  isOwn: boolean;
  displayName: string | null;
  notes: string | null;
  followersCount: number | null;
  mediaCount: number | null;
  lastRefreshedAt: string | null;
  lastRefreshError: string | null;
  createdAt: string | null;
}

export interface CompetitorRow extends WatchedAccount {
  followersDelta: number | null;
  insights: CompetitorInsights | null;
}

export interface CompetitorsInsightsResponse {
  windowDays: number;
  own: CompetitorRow | null;
  competitors: CompetitorRow[];
}

const key = (brandId: string) => ["competitors", brandId] as const;

export function useCompetitorInsights(brandId: string, days: number) {
  return useQuery({
    queryKey: [...key(brandId), "insights", days],
    queryFn: () =>
      apiFetch<CompetitorsInsightsResponse>(`/brands/${brandId}/competitors/insights?days=${days}`),
    enabled: !!brandId,
  });
}

export function useAddCompetitor(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { username: string; notes?: string }) =>
      apiFetch<WatchedAccount>(`/brands/${brandId}/competitors`, {
        method: "POST",
        body: JSON.stringify(input),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(brandId) }),
  });
}

export function useUpdateCompetitorNotes(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (input: { id: string; notes: string | null }) =>
      apiFetch<WatchedAccount>(`/brands/${brandId}/competitors/${input.id}`, {
        method: "PATCH",
        body: JSON.stringify({ notes: input.notes }),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(brandId) }),
  });
}

export function useRemoveCompetitor(brandId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) =>
      apiFetch<{ id: string; deleted: true }>(`/brands/${brandId}/competitors/${id}`, { method: "DELETE" }),
    onSuccess: () => qc.invalidateQueries({ queryKey: key(brandId) }),
  });
}

export function useRefreshCompetitors(brandId: string) {
  return useMutation({
    mutationFn: () =>
      apiFetch<{ jobId: string }>(`/brands/${brandId}/competitors/refresh`, { method: "POST" }),
  });
}
